import { dsComponentRegistry } from "../registry"

/**
 * Returns a React component for a given design system container.
 *
 * Looks up the container's dsComponentType in the registry, maps its children and props,
 * and returns the corresponding React element.
 *
 * @param {Object} container - The container object, expected to have dsComponentType, :itemsOrder, and :items.
 * @return {React.ReactElement|null} The mapped React component, or null if not found or not mappable.
 */
export function getDSComponentForContainer(container) {
  try {
    const key = resolveDSKey(container)

    if (!key) return null
    const mapping = dsComponentRegistry[key]
    if (!mapping) return null

    const { component: DSComponent, mapper } = mapping

    if (typeof mapper !== "function") return null

    const children =
      container?.[":itemsOrder"]
        ?.map(key => container?.[":items"]?.[key])
        .filter(Boolean) || []
    const props = mapper(container, children)

    if (!props) return null

    return <DSComponent {...props} />
  } catch (error) {
    console.error("Error in getDSComponentForContainer:", error)
    return null
  }
}

/**
 * Resolves the registry key for a DS container.
 *
 * Prefers an explicit `dsComponentType` that exists in the registry.
 * Falls back to `containerId`. Returns `undefined` if neither is usable.
 *
 * @param {Object} container - The container to inspect.
 * @return {string|undefined} A registry key or `undefined` if unresolved.
 */
function resolveDSKey(container) {
  const fromExplicit = container?.dsComponentType

  if (fromExplicit && dsComponentRegistry[fromExplicit]) return fromExplicit

  const fromId = container?.containerId
  if (fromId && dsComponentRegistry[fromId]) return fromId

  return undefined
}
